import { useEffect, useState } from "react";
import { Languages, Search, Loader2, Sparkles } from "lucide-react";
import PageShell from "../components/PageShell";
import { getWordStudy, getWordStudyPopular } from "../api";
import { track } from "../lib/analytics";

const LANG_STYLE = {
  Greek: "from-[#3E5A6B] to-[#2E5A66]",
  Hebrew: "from-[#A06A48] to-[#5A4631]",
  Aramaic: "from-[#5C8A6E] to-[#2E5A66]",
};

function OriginalCard({ o }) {
  return (
    <div data-testid="word-original-card" className="rounded-2xl border border-border bg-background/40 p-5 backdrop-blur-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <span className={`font-ui inline-block rounded-full bg-gradient-to-r ${LANG_STYLE[o.language] || "from-primary/40 to-primary/20"} px-3 py-1 text-[0.6rem] font-semibold uppercase tracking-[0.15em] text-white`}>
            {o.language}{o.testament ? ` · ${o.testament}` : ""}
          </span>
          <p className="font-serif-display mt-3 text-3xl leading-tight text-foreground" dir="auto">{o.original}</p>
          <p className="font-ui mt-1 text-base text-primary">{o.translit}</p>
          {o.pronunciation && <p className="font-ui text-xs text-muted-foreground">{o.pronunciation}</p>}
        </div>
        {o.strongs && (
          <span className="font-ui shrink-0 rounded-lg border border-border px-2 py-1 text-xs font-semibold text-muted-foreground">{o.strongs}</span>
        )}
      </div>
      <p className="reading-text mt-3 text-sm leading-relaxed text-foreground/85">{o.meaning}</p>
      {Array.isArray(o.verses) && o.verses.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {o.verses.map((v) => (
            <span key={v} className="font-ui rounded-full bg-muted px-3 py-1 text-xs text-foreground/80">{v}</span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function WordStudy() {
  const [popular, setPopular] = useState([]);
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { getWordStudyPopular().then(setPopular).catch(() => {}); }, []);

  const run = async (word) => {
    const w = (word || "").trim();
    if (!w) return;
    setLoading(true); setErr(""); setData(null);
    track("word_study_search", { word: w.toLowerCase() });
    try {
      const res = await getWordStudy(w);
      setData(res);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn’t load that word. Try another.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell
      title="Word Study"
      subtitle="See what a word meant in the original Hebrew, Greek & Aramaic"
      icon={Languages}
      accent={{ a: "#C2A368", b: "#3E5A6B", c: "#2E5A66" }}
      testid="word-study-page"
    >
      <form
        onSubmit={(e) => { e.preventDefault(); run(q); }}
        className="relative mb-5"
        data-testid="word-study-form"
      >
        <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
        <input
          data-testid="word-study-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search a word — e.g. love, grace, peace…"
          className="font-ui w-full rounded-full border border-border bg-background/50 py-4 pl-12 pr-28 text-base outline-none backdrop-blur-md transition-colors focus:border-primary"
        />
        <button
          type="submit"
          data-testid="word-study-search-btn"
          disabled={loading || !q.trim()}
          className="font-ui absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Study"}
        </button>
      </form>

      {/* popular chips */}
      <div className="mb-8 flex flex-wrap gap-2" data-testid="word-study-popular">
        {popular.map((w) => (
          <button
            key={w}
            data-testid={`popular-word-${w}`}
            onClick={() => { setQ(w); run(w); }}
            className="font-ui rounded-full border border-border bg-background/40 px-4 py-2 text-sm capitalize text-foreground/85 backdrop-blur-sm transition-colors hover:border-primary/50 hover:bg-muted"
          >
            {w}
          </button>
        ))}
      </div>

      {loading && (
        <div className="flex flex-col items-center py-16 text-center" data-testid="word-study-loading">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="font-ui mt-4 text-sm text-muted-foreground">Studying the original languages…</p>
        </div>
      )}

      {err && !loading && (
        <p className="font-ui rounded-2xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive" data-testid="word-study-error">{err}</p>
      )}

      {data && !loading && (
        <div className="space-y-5 animate-in fade-in" data-testid="word-study-result">
          <div>
            <h2 className="font-serif-display text-4xl capitalize text-foreground">{data.word}</h2>
            <p className="reading-text mt-2 text-base leading-relaxed text-foreground/85">{data.summary}</p>
          </div>
          <div className="space-y-4">
            {(data.originals || []).map((o, idx) => <OriginalCard key={idx} o={o} />)}
          </div>
          {data.reflection && (
            <div className="rounded-2xl border border-primary/25 bg-primary/5 p-5">
              <div className="mb-1 flex items-center gap-2 text-primary">
                <Sparkles className="h-4 w-4" />
                <span className="font-ui text-xs font-semibold uppercase tracking-[0.15em]">Reflect</span>
              </div>
              <p className="reading-text text-sm leading-relaxed text-foreground/90">{data.reflection}</p>
            </div>
          )}
          <p className="font-ui pt-2 text-center text-xs text-muted-foreground">
            Original-language insights are AI-assisted — always weigh them against Scripture.
          </p>
        </div>
      )}

      {!data && !loading && !err && (
        <p className="font-ui py-10 text-center text-sm text-muted-foreground" data-testid="word-study-empty">
          Search a word above, or tap one of the popular words to begin.
        </p>
      )}
    </PageShell>
  );
}
